import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {FormsModule, ReactiveFormsModule, UntypedFormControl, Validators} from '@angular/forms';
import {BarCode} from '../../../../generic/classes/barcode';
import {ShoppingCartLine} from '../../../../generic/classes/shoppingcartline';
import {ApplicationService} from '../../../../generic/service/application.service';
import {BarcodeService} from '../../../../service/barcode.service';

/**
 * Uses an attribute selector since the extra element will destroy the table
 */
@Component({
	selector: '[add-barcode-row]',
	templateUrl: 'add.barcode.row.component.html',
	standalone: true,
	imports: [FormsModule, ReactiveFormsModule],
})
export class AddBarcodeRowComponent implements OnInit {
	@Input() shoppingCartLine: ShoppingCartLine;

	@Output() addBarCode: EventEmitter<BarCode>;

	descriptionControl: UntypedFormControl;
	barcodeControl: UntypedFormControl;
	salesPriceControl: UntypedFormControl;

	barCode: BarCode = new BarCode();

	barcodeDisabled: boolean = false; // Indicates if this barcode is still editable.
	showBarcodeInformation: boolean = false;
	showCurrency: boolean = false;
	confirmed: boolean = false;
	loading: boolean = false;

	constructor(
		public applicationService: ApplicationService,
		private barcodeService: BarcodeService,
	) {
		this.addBarCode = new EventEmitter<BarCode>();
	}

	ngOnInit() {
		/*
			Als waarde = -1
			Er is nog geen barcode voorstel gedaan door Navision. Barcode informatie niet tonen.

			Als waarde = 0
			Barcode voorstel wordt aangemaakt. Barcode informatie niet tonen. Deze status zal in praktijk niet voorkomen,
			omdat ie al snel naar de volgende status springt

			Als waarde = 1
			Barcode voorstel is aangemaakt. Barcode informatie tonen en door klant nu aan te vullen/invoeren en te bevestigen.

			Als waarde >1
			Barcode informatie bevestigd door klant. Barcode informatie nog wel tonen, maar niet meer aan te passen
			en niet meer te bevestigen.
		 */

		this.showBarcodeInformation = this.shoppingCartLine.bcStatus === 1 || this.shoppingCartLine.bcStatus > 1;
		this.barcodeDisabled = this.shoppingCartLine.bcStatus !== 1;

		this.constructBarcode();

		// Determine if the currency needs to be shown
		this.showCurrency = !this.barCode.salesPrice ? false : true;

		// Initialize form controls
		this.descriptionControl = new UntypedFormControl(this.barCode.description,);
		this.barcodeControl = new UntypedFormControl(this.barCode.barcode);

		/*
		 * This regular expression checks the input based on the allowed value in the stored procedure.
		 * It will only allow a decimal values. Explanation of the full regex:
		 * '^' - Asserts position at the start of a line
		 * '[\d]{1,16}' - Matches between 1 and 16 times, as many times as possible with numbers between 0 and 9
		 * '[\,\.]' - Matches a , or . literally (comma's are replaced with . in submit and blur events
		 * '([\d]{2})' - Allows a number between 0 and 9 and matches only 2 times (decimals)
		 * '|' - starts the 2nd alternative regex group
		 * '([\d]{1,16})' - Allows characters between 0 and 9 and matches 16 times (no decimals)
		 * '?' - Matches between 0 and one time, as many times as possible
		 * '$' - Asserts position at the end of a line
		 *
		 * So a user can only input values like: 0.00, 12, 12.00, 12000.00, 12000, 124.43
		 * But can not input values like: 1.000.00, 1.000,00, 1.000
		 */
		this.salesPriceControl = new UntypedFormControl(
			this.barCode.salesPrice,
			Validators.pattern(
				'^([\\d]{1,16}[\\,\\.]([\\d]{2})|(([\\d]{1,16})))?$',
			),
		);

		// Listening for changes
		this.initOnBarcodeChange();
	}

	setLoadingAndDisableControls() {
		this.loading = true;

		this.descriptionControl.disable();
		this.barcodeControl.disable();
		this.salesPriceControl.disable();
	}

	unsetLoadingAndEnableControls() {
		this.loading = false;

		this.descriptionControl.enable();
		this.barcodeControl.enable();
		this.salesPriceControl.enable();
	}

	confirmBarcode() {
		if (
			this.descriptionControl.valid &&
			this.barcodeControl.valid &&
			this.salesPriceControl.valid
		) {
			// Check if the price input is null or empty and calculate the price based on
			// product price and quantity selected. Otherwise, get the input value and use that.
			if (
				this.salesPriceControl.value === null ||
				this.salesPriceControl.value === ''
			) {
				this.barCode.salesPrice = this.getPriceForLine().toFixed(2);
				this.salesPriceControl.setValue(this.barCode.salesPrice);
			}

			this.setLoadingAndDisableControls();

			const promise = this.barcodeService.addBarcode(this.shoppingCartLine);

			promise.then(() => {
				this.confirmed = true;
				this.unsetLoadingAndEnableControls();
			});

			promise.catch(() => this.unsetLoadingAndEnableControls());
		}
	}

	/**
	 * Calculates the total price for the given shopping cart line
	 *
	 * @returns {number}
	 */
	getPriceForLine(): number {
		return (this.shoppingCartLine.unitPriceExcResale * this.shoppingCartLine.quantity);
	}

	initOnBarcodeChange() {
		this.onBarcodeChange();
		this.onDescriptionChange();
		this.onSalesPriceChange();
	}

	onBarcodeChange() {
		this.barcodeControl.valueChanges.subscribe(value => {
			this.barCode.barcode = value;
			this.addBarCode.emit(this.barCode);
		});
	}

	onDescriptionChange() {
		this.descriptionControl.valueChanges.subscribe(value => {
			this.barCode.description = value;
			this.addBarCode.emit(this.barCode);
		});
	}

	onSalesPriceBlur() {
		const value = this.salesPriceControl.value;

		if (!value) {
			return;
		}

		const fixedValue = value.replace(/,/g, '.');

		if (value === fixedValue) {
			return;
		}

		this.salesPriceControl.setValue(fixedValue);
	}

	onSalesPriceChange() {
		this.salesPriceControl.valueChanges.subscribe(value => {
			if (!value) {
				return;
			}

			this.barCode.salesPrice = value.replace(/,/g, '.');
			this.addBarCode.emit(this.barCode);
		});
	}

	/**
	 * To be called from parent to indicate a change in factor or rounding
	 * This function doesn't round in all cases. In the case of 0.09 and 9
	 * it should top to an amount ending with x.x9 and x9.00 respectively
	 * @param factor
	 * @param rounding
	 */
	reCalculatePrice(factor: number, rounding: number) {
		let originalPrice = +this.barCode.originalPrice;

		if (originalPrice === 0) {
			originalPrice = this.shoppingCartLine.unitPriceExcResale;
		}

		const barcodePrice = factor * originalPrice;
		let calculatedPrice: number;

		switch (rounding) {
			case 0.8:
			case 0.09:
			case 0.9:
			case 0.95:
			case 0.99:
			case 9:
				calculatedPrice = this.calculateToppedOfPrice(barcodePrice, rounding);
				break;
			default:
				calculatedPrice = this.calculateRoundedPrice(barcodePrice, rounding);
		}
		this.salesPriceControl.setValue(calculatedPrice.toFixed(2));
	}

	/**
        When rounding is greater than 1 truncate the price and set the end digit to
        the value of rounding.
        When rounding is less then price is rounded to nearest decimal-1 and rounding
        is added.
	 */
	private calculateToppedOfPrice(price: number, rounding: number): number {
		if (rounding > 1) {
			const truncatedPrice = Math.trunc(price);
			const difference = rounding - (truncatedPrice % 10);
			return difference === 0 ? truncatedPrice + 10 : truncatedPrice + difference;
		}

		const roundedPrice = rounding < 0.1 ? Math.round(price * 10) / 10 : Math.round(price);

		return roundedPrice + rounding;
	}

	private calculateRoundedPrice(price: number, rounding: number): number {
		const moduloPrice = price % rounding;
		return moduloPrice === 0 ? price : price - moduloPrice + rounding;
	}

	/**
	 * Shorthand to keep ngOnInit readable
	 */
	private constructBarcode() {
		this.barCode.shipToCode = this.shoppingCartLine.shipToCode;
		this.barCode.shipmentDate = this.shoppingCartLine.shipmentDate;
		this.barCode.shoppingCartNo = this.shoppingCartLine.shoppingCartNo;

		// Set initial values - only use barcode suggestion if none is set yet
		this.barCode.barcode = this.shoppingCartLine.bcBarcode
			? this.shoppingCartLine.bcBarcode
			: this.shoppingCartLine.barcode;

		this.barCode.description = this.shoppingCartLine.bcBarcodeDescription;

		// limit number of digits
		const price: number = +this.shoppingCartLine.bcBarcodePrice;

		// Don't add the salesPrice if the bcBarcodePrice field is empty
		if (price && price > 0) {
			this.barCode.salesPrice = price.toFixed(2);
			this.barCode.originalPrice = price.toFixed(2);
		}
	}
}
